import FormAccordions from "@components/core/accordions/FormAccordions";
import PageLayout from "@components/core/layouts/PageLayout";
import InfoModal from "@components/core/modals/InfoModal";
import CardSection from "@components/core/section/CardSection";
import { ROUTES } from "@routes/Routes";
import AttachmentsSection from "@views/core/extranet/transactions/signature/sections/AttachmentsSection";
import FilesToSignSection from "@views/core/extranet/transactions/signature/sections/FilesToSignSection";
import OfficeChoiceSection from "@views/core/extranet/transactions/signature/sections/OfficeChoiceSection";
import SignatoriesSection from "@views/core/extranet/transactions/signature/sections/SignatoriesSection/SignatoriesSection";
import SignatureMessageSection from "@views/core/extranet/transactions/signature/sections/SignatureMessageSection";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import useTransactionScreen from "@views/core/extranet/transactions/useTransactionScreen";

export interface SignatureScreenSectionProps {
  onSubmit?: () => void;
}

const SignatureScreen: FunctionComponent = () => {
  const { t } = useTranslation();

  const {
    activeBadge,
    setActiveBadge,
    handleChangeBadge,
    preselectedOfficeId,
    me,
    isLoading,
    invitation,
    endOfTransaction,
    isSignaturesFromModel,
    activateAttachment,
    attachments,
    signatures,
    files,
  } = useTransactionScreen("signature");

  const displayAttachments =
    activateAttachment || (attachments && attachments.length > 0);
  const currentFileCount = files.length + attachments.length;

  return (
    <PageLayout
      container
      returnRoute={ROUTES.extranet.home.generate()}
      hideDropdown
    >
      <CardSection title={t("signature.title")}>
        <FormAccordions
          activeTab={activeBadge}
          setActiveTab={setActiveBadge}
          sections={[
            {
              key: "1",
              title: t("signature.accordion.officeChoice"),
              content: (
                <OfficeChoiceSection
                  onSubmit={() => handleChangeBadge(1)}
                  preselectedOfficeId={preselectedOfficeId}
                  disabled={isSignaturesFromModel}
                />
              ),
            },
            {
              key: "2",
              title: t("signature.accordion.document"),
              content: (
                <FilesToSignSection
                  onSubmit={() => handleChangeBadge(2)}
                  isSignature
                  description={t(
                    "signature.accordion.documentForm.description",
                  )}
                  descriptionSeparator={t(
                    "signature.accordion.documentForm.descriptionSeparator",
                  )}
                  descriptionUrlTitle={t(
                    "signature.accordion.documentForm.descriptionUrlTitle",
                  )}
                  currentFileCount={currentFileCount}
                />
              ),
            },
            ...(displayAttachments
              ? [
                  {
                    key: "3",
                    title: (
                      <>
                        {t("signature.accordion.attachments")}
                        <InfoModal value="attachment" info />
                      </>
                    ),
                    content: (
                      <AttachmentsSection
                        attachments={attachments}
                        onSubmit={() => handleChangeBadge(3)}
                        description={t(
                          "signature.accordion.attachmentForm.description",
                        )}
                        descriptionSeparator={t(
                          "signature.accordion.attachmentForm.descriptionSeparator",
                        )}
                        descriptionUrlTitle={t(
                          "signature.accordion.attachmentForm.descriptionUrlTitle",
                        )}
                        currentFileCount={currentFileCount}
                      />
                    ),
                  },
                ]
              : []),
            {
              key: displayAttachments ? "4" : "3",
              title: t("signature.accordion.signatory"),
              content: (
                <SignatoriesSection
                  onSubmit={() => handleChangeBadge(displayAttachments ? 4 : 3)}
                  me={me}
                  signatures={signatures}
                />
              ),
            },
            {
              key: displayAttachments ? "5" : "4",
              title: t("signature.accordion.parameters"),
              content: (
                <SignatureMessageSection
                  officeId={preselectedOfficeId}
                  isLoading={isLoading}
                  invitation={invitation}
                  endOfTransaction={endOfTransaction}
                  isSignaturesFromModel={isSignaturesFromModel}
                />
              ),
            },
          ]}
        />
      </CardSection>
    </PageLayout>
  );
};

export default SignatureScreen;
